import type { Alert } from '@/types';
import { normalizeAlertSeverity } from '@/utils/alertSeverity';
import { apiClient } from './client';

export interface AlertsParams {
  facilityId: string;
  /** open = not resolved (acknowledged or not); active = neither acknowledged nor resolved. */
  status?: 'open' | 'active' | 'acknowledged' | 'resolved';
  severity?: Alert['severity'] | Uppercase<Alert['severity']>;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface AlertStats {
  period_days: number;
  total: number;
  by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  /** Raised in the window and not acknowledged / not resolved. */
  unacknowledged: number;
  unresolved: number;
  /** Open now (not resolved), whenever raised. */
  open_unresolved?: number;
  /** Open now and not yet acknowledged, whenever raised. */
  open_unacknowledged?: number;
  /** The facility's IANA time zone. */
  timezone?: string;
}

export interface AlertHistoryPoint {
  /** Site-local day (or week Monday / month start) as YYYY-MM-DD. */
  period: string;
  CRITICAL: number;
  HIGH: number;
  MEDIUM: number;
  LOW: number;
  total: number;
}

export interface AlertHistory {
  trend: AlertHistoryPoint[];
  summary: {
    total_alerts: number;
    avg_per_day: number;
    peak_day: string | null;
    peak_day_count: number;
    days_analyzed: number;
    granularity: 'day' | 'week' | 'month';
    /** First and last site day of the window (YYYY-MM-DD). */
    first_day?: string;
    last_day?: string;
    timezone?: string;
  };
}

export interface ProximityEvent {
  id: string;
  facilityId: string;
  vehicleId: string | null;
  workerId: string | null;
  vehicleName: string | null;
  workerName: string | null;
  distanceM: number;
  timeToCollisionS: number | null;
  workerSpeedMps: number | null;
  vehicleSpeedMps: number | null;
  workerPosition: { x: number; y: number } | null;
  vehiclePosition: { x: number; y: number } | null;
  severity: string;
  eventType: string;
  alertTriggered: boolean;
  acknowledged: boolean;
  acknowledgedBy: string | null;
  acknowledgedAt: string | null;
  zoneName: string | null;
  timestamp: string;
}

export interface ProximityEventsParams {
  startDate?: string;
  endDate?: string;
  workerId?: string;
  vehicleId?: string;
  limit?: number;
}

export interface ActiveProximityEventsParams {
  limit?: number;
  windowMinutes?: number;
}

export interface SafetyLightState {
  anchorId: string;
  facilityId: string;
  name: string;
  anchorRole: string | null;
  /** The light's last reported state; null when it has never reported one. */
  state: string | null;
  reason: string | null;
  triggeredByVehicleId: string | null;
  distanceToVehicleM: number | null;
  position: {
    x: number | null;
    y: number | null;
    floor: string | null;
  };
  /** When the state was reported; null when it never was. */
  updatedAt: string | null;
  /** The state was reported more than 10 minutes ago. */
  stale?: boolean;
}

function normalizeAlertRecord(alert: Alert): Alert {
  const severity = normalizeAlertSeverity(alert.severity);
  return severity ? { ...alert, severity } : alert;
}

function buildAlertsQuery(params: AlertsParams): { facilityId: string; query: Omit<AlertsParams, 'facilityId'> } {
  const { facilityId, ...rest } = params;
  const query: Omit<AlertsParams, 'facilityId'> = { ...rest };
  if (query.severity) {
    query.severity = normalizeAlertSeverity(query.severity) ?? undefined;
  } else {
    delete query.severity;
  }
  return { facilityId, query };
}

/** Lists facility alerts with canonical lowercase severity and legacy-case response normalization via GET /facilities/:id/alerts. */
export async function getAlerts(params: AlertsParams): Promise<Alert[]> {
  const { facilityId, query } = buildAlertsQuery(params);
  const res = await apiClient.get(`/facilities/${facilityId}/alerts`, {
    params: query,
  });
  return ((res.data.data ?? res.data) as Alert[]).map(normalizeAlertRecord);
}

export interface AlertsPage {
  alerts: Alert[];
  /** Server-side count of every alert matching the filter, independent of `limit`; null when the response lacks pagination. */
  total: number | null;
}

/** Lists facility alerts plus the filter-respecting server total (`pagination.total`) via GET /facilities/:id/alerts. */
export async function getAlertsPage(params: AlertsParams): Promise<AlertsPage> {
  const { facilityId, query } = buildAlertsQuery(params);
  const res = await apiClient.get(`/facilities/${facilityId}/alerts`, {
    params: query,
  });
  const alerts = ((res.data.data ?? res.data) as Alert[]).map(normalizeAlertRecord);
  const rawTotal = res.data?.pagination?.total;
  const total = typeof rawTotal === 'number' && Number.isFinite(rawTotal) ? rawTotal : null;
  return { alerts, total };
}

/** Acknowledges an alert via POST /facilities/:id/alerts/:alertId/acknowledge. */
export async function acknowledgeAlert(
  facilityId: string,
  alertId: string,
): Promise<Alert> {
  const res = await apiClient.post(
    `/facilities/${facilityId}/alerts/${alertId}/acknowledge`,
    {},
  );
  return (res.data.data ?? res.data) as Alert;
}

/** Resolves an alert via POST /facilities/:id/alerts/:alertId/resolve. */
export async function resolveAlert(
  facilityId: string,
  alertId: string,
): Promise<Alert> {
  const res = await apiClient.post(
    `/facilities/${facilityId}/alerts/${alertId}/resolve`,
    {},
  );
  return (res.data.data ?? res.data) as Alert;
}

/** Fetches severity/status counts via GET /facilities/:id/alerts/stats. */
export async function getAlertStats(
  facilityId: string,
  params: { days?: number } = {},
): Promise<AlertStats> {
  const res = await apiClient.get(`/facilities/${facilityId}/alerts/stats`, { params });
  return (res.data.data ?? res.data) as AlertStats;
}

/** Fetches alert count trend data via GET /facilities/:id/alerts/history. */
export async function getAlertHistory(
  facilityId: string,
  params: { days?: number; granularity?: 'day' | 'week' | 'month' } = {},
): Promise<AlertHistory> {
  const res = await apiClient.get(`/facilities/${facilityId}/alerts/history`, { params });
  return (res.data.data ?? res.data) as AlertHistory;
}

/** Fetches active worker-vehicle proximity warnings via GET /facilities/:id/proximity-events/active. */
export async function getActiveProximityEvents(
  facilityId: string,
  params: ActiveProximityEventsParams = {},
): Promise<ProximityEvent[]> {
  const res = await apiClient.get(`/facilities/${facilityId}/proximity-events/active`, { params });
  return (res.data.data ?? res.data) as ProximityEvent[];
}

/** Fetches worker-vehicle proximity history via GET /facilities/:id/proximity-events. */
export async function getProximityEvents(
  facilityId: string,
  params: ProximityEventsParams = {},
): Promise<ProximityEvent[]> {
  const res = await apiClient.get(`/facilities/${facilityId}/proximity-events`, { params });
  return (res.data.data ?? res.data) as ProximityEvent[];
}

/** Fetches smart safety-light states via GET /facilities/:id/safety-lights. */
export async function getSafetyLights(facilityId: string): Promise<SafetyLightState[]> {
  const res = await apiClient.get(`/facilities/${facilityId}/safety-lights`);
  return (res.data.data ?? res.data) as SafetyLightState[];
}
